import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'

/**
 * Context for sharing section-specific data between the content area and the map
 * This allows sections to push their data to be displayed on the map
 */

export type SectionType =
  | 'purchase_listings'
  | 'rental_listings'
  | 'transactions'
  | 'building_permits'
  | 'amenities'
  | 'transport'
  | 'demographics'
  | 'employment'
  | 'history'
  | 'energy'
  | 'sunlight'
  | 'sunlight_study'
  | 'purchase_market'
  | 'rental_market'
  | 'noise'
  | 'taxes'
  | 'electricity'
  | null

interface MapSectionData {
  listings?: any[]
  transactions?: any[]
  building_permits?: any[]
  amenities?: any
  transport?: {
    publicTransportStops?: any[]
    isochrones?: {
      walking?: any
      cycling?: any
      driving?: any
    }
    activeMobilityMetrics?: {
      evStationsNearby?: any[]
    }
    roadAccessMetrics?: {
      allHighwayAccess?: any[]
    }
    services?: any
  }
  [key: string]: any
}

interface MapSectionDataContextState {
  /** Current active section */
  activeSection: SectionType

  /** Section-specific data (e.g., listings, transactions) */
  sectionData: MapSectionData

  /** ID of currently hovered listing/item */
  hoveredItemId: string | number | null

  /** ID of currently selected listing/item */
  selectedItemId: string | number | null

  /** Coordinates to fly to on map [lon, lat] */
  flyToCoordinates: [number, number] | null

  /** Handler for when a listing/item is clicked (opens detail panel) */
  onItemClick: ((item: any) => void) | null

  /** Update the active section */
  setActiveSection: (section: SectionType) => void

  /** Update section data (partial updates supported) */
  updateSectionData: (data: Partial<MapSectionData>) => void

  /** Clear all section data */
  clearSectionData: () => void

  /** Set hovered item ID */
  setHoveredItemId: (id: string | number | null) => void

  /** Set selected item ID */
  setSelectedItemId: (id: string | number | null) => void

  /** Set coordinates to fly to on map */
  setFlyToCoordinates: (coordinates: [number, number] | null) => void

  /** Set item click handler */
  setOnItemClick: (handler: ((item: any) => void) | null) => void
}

const MapSectionDataContext = createContext<
  MapSectionDataContextState | undefined
>(undefined)

interface MapSectionDataProviderProps {
  children: ReactNode
}

export const MapSectionDataProvider: React.FC<MapSectionDataProviderProps> = ({
  children,
}) => {
  const [activeSection, setActiveSection] = useState<SectionType>(null)
  const [sectionData, setSectionData] = useState<MapSectionData>({})
  const [hoveredItemId, setHoveredItemId] = useState<string | number | null>(
    null
  )
  const [selectedItemId, setSelectedItemId] = useState<string | number | null>(
    null
  )
  const [flyToCoordinates, setFlyToCoordinates] = useState<
    [number, number] | null
  >(null)
  const [onItemClick, setOnItemClick] = useState<((item: any) => void) | null>(
    null
  )

  const updateSectionData = useCallback((data: Partial<MapSectionData>) => {
    setSectionData((prev) => ({ ...prev, ...data }))
  }, [])

  const clearSectionData = useCallback(() => {
    setSectionData({})
  }, [])

  const contextValue: MapSectionDataContextState = {
    activeSection,
    sectionData,
    hoveredItemId,
    selectedItemId,
    flyToCoordinates,
    onItemClick,
    setActiveSection,
    updateSectionData,
    clearSectionData,
    setHoveredItemId,
    setSelectedItemId,
    setFlyToCoordinates,
    setOnItemClick,
  }

  return (
    <MapSectionDataContext.Provider value={contextValue}>
      {children}
    </MapSectionDataContext.Provider>
  )
}

// Custom hook for consuming the context
export const useMapSectionData = (): MapSectionDataContextState => {
  const context = useContext(MapSectionDataContext)
  if (context === undefined) {
    throw new Error(
      'useMapSectionData must be used within a MapSectionDataProvider'
    )
  }
  return context
}
